import type React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Linkedin } from "lucide-react";
import { type Admin, type OutreachProspect } from "@lumika/shared";
import {
  Badge,
  Button,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import {
  ProspectFormFields,
  ProspectFormSchema,
  toProspectEditValues,
  toProspectPayload,
} from "./ProspectForm";
import { STAGE_LABELS, STAGE_VARIANT } from "./outreach-ui";
import { ActivityLog } from "./ActivityLog";

export function OutreachProspectDetailPage() {
  const { prospectId } = useParams();
  const adminsQuery = useQuery({ queryKey: ["admins"], queryFn: adminApi.listAdmins });
  const prospectQuery = useQuery({
    queryKey: ["outreach", "prospect", prospectId],
    queryFn: () => adminApi.getOutreachProspect(prospectId!),
    enabled: Boolean(prospectId),
  });

  if (prospectQuery.isLoading) {
    return (
      <div className="p-6">
        <PageLoading message="Loading prospect…" />
      </div>
    );
  }
  if (prospectQuery.isError || !prospectQuery.data) {
    return (
      <div className="p-6">
        <PageError
          title="Couldn't load prospect"
          description="It may have been deleted."
          action={
            <Button asChild variant="outline">
              <Link to="/outreach">Back to Outreach</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const prospect = prospectQuery.data;
  const admins = adminsQuery.data ?? [];

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <Link
          to="/outreach"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Outreach
        </Link>
        <PageHeader
          eyebrow="Prospect"
          title={prospect.companyName}
          description={<QuickFacts prospect={prospect} />}
          actions={<Badge variant={STAGE_VARIANT[prospect.stage]}>{STAGE_LABELS[prospect.stage]}</Badge>}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
        {/* updatedAt key: after a save the refetched prospect remounts the
            editor so its baseline (and dirty state) reset to the saved values. */}
        <ProspectEditor key={prospect.updatedAt} prospect={prospect} admins={admins} />
        <ActivityLog prospectId={prospect.id} admins={admins} />
      </div>
    </div>
  );
}

function QuickFacts({ prospect }: { prospect: OutreachProspect }) {
  const facts: React.ReactNode[] = [];
  if (prospect.contactName) facts.push(prospect.contactName);
  if (prospect.email) facts.push(prospect.email);
  if (prospect.phone) facts.push(prospect.phone);
  if (prospect.websiteUrl) {
    const href = /^https?:\/\//i.test(prospect.websiteUrl)
      ? prospect.websiteUrl
      : `https://${prospect.websiteUrl}`;
    facts.push(
      <a
        key="site"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
      >
        {prospect.websiteUrl}
        <ExternalLink className="h-3 w-3" />
      </a>,
    );
  }
  if (prospect.linkedinUrl) {
    const href = /^https?:\/\//i.test(prospect.linkedinUrl)
      ? prospect.linkedinUrl
      : `https://${prospect.linkedinUrl}`;
    facts.push(
      <a
        key="li"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
      >
        <Linkedin className="h-3 w-3" />
        LinkedIn
      </a>,
    );
  }
  if (facts.length === 0) return "No contact details yet.";
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {facts.map((f, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-muted-foreground/40">·</span>}
          {f}
        </span>
      ))}
    </span>
  );
}

function ProspectEditor({ prospect, admins }: { prospect: OutreachProspect; admins: Admin[] }) {
  const qc = useQueryClient();
  const { values, errors, setField, validate } = useZodForm(
    ProspectFormSchema,
    toProspectEditValues(prospect),
  );

  const updateMutation = useMutation({
    mutationFn: () => adminApi.updateOutreachProspect(prospect.id, toProspectPayload(values)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["outreach"] });
      toast.success("Saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const dirty =
    JSON.stringify(values) !== JSON.stringify(toProspectEditValues(prospect));

  function save() {
    if (!validate().ok) return;
    updateMutation.mutate();
  }

  return (
    <Section
      title="Details"
      actions={
        <Button size="sm" onClick={save} disabled={!dirty || updateMutation.isPending}>
          {updateMutation.isPending ? "Saving…" : "Save"}
        </Button>
      }
    >
      <ProspectFormFields values={values} errors={errors} setField={setField} admins={admins} />
    </Section>
  );
}

